import { Box, HStack, Radio, RadioGroup, Spacer, Stack, Text, Textarea, VStack } from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { SubmitStepButton } from './SubmitStepButton';

import { Input } from '@chakra-ui/react';
import { ThemeProvider, createTheme } from '@mui/material';
import { DataGrid, GridRowSelectionModel, GridRowsProp, GridSortModel } from '@mui/x-data-grid';
import { useUsers } from '@partiu/api/dashboard/hooks';
import { AdaptiveStack } from '@partiu/ui/components/atoms/AdaptiveStack.tsx';
import { Button } from '@partiu/ui/components/atoms/Button';
import { Card } from '@partiu/ui/components/atoms/Card.tsx';
import { Labeled } from '@partiu/ui/components/molecules/Labeled.tsx';
import { DataGridCPFFormatter } from '../../../formatters';
import { useNotificationCentralContext } from '../notification-central.context';
import { IStep } from './interfaces';
import { useTheme } from '@partiu/ui/contexts/ThemeProvider';

export function ChooseReceiverStep({ HeaderComponent, onSubmit }: IStep) {
  const { setFormData, formData } = useNotificationCentralContext();

  const { totalUsers = 0, totalImportedUsers = 0 } = useTheme();

  const [sendMethod, setSendMethod] = useState<string>(formData?.sendTo);
  const [userListChoice, setUserListChoice] = useState<string>('all-users');
  const [emailList, setEmailList] = useState(formData?.emailsList.join('\n') || '');
  const [users, setUsers] = useState<GridRowSelectionModel>([]);

  const [file, setFile] = useState(null);

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleSubmit = () => {
    setFormData((oldData) => {
      return {
        ...oldData,
        emailsList: emailList.split('\n').filter((s) => s !== ''),
        sendTo: userListChoice === 'specific-users' ? 'specific-users' : sendMethod,
        users,
        fileEmails: file,
      };
    });
    onSubmit();
  };

  return (
    <>
      <HeaderComponent title={'Destinatários'} />
      <HStack width={'100%'} alignItems={'flex-start'}>
        <Box paddingY={2} width={{ base: '100%', lg: '600px' }}>
          <Text fontWeight={'bold'} marginBottom={4}>
            Selecione abaixo os destinatátios
          </Text>
          <RadioGroup onChange={setSendMethod} value={sendMethod}>
            <Stack>
              <Radio value="manual-list" isDisabled={formData.notificationType !== 'email'}>
                Inserir lista de email
              </Radio>
              <Radio value="all-users">Meus clientes - Base Partiu</Radio>
              {sendMethod === 'all-users' && (
                <Box marginLeft={'16px'}>
                  <RadioGroup onChange={setUserListChoice} value={userListChoice}>
                    <Stack>
                      <Radio value="all-users">Todos os usuários ({totalUsers} usuarios)</Radio>
                      <Radio value="specific-users">Usuários específicos</Radio>
                    </Stack>
                  </RadioGroup>
                </Box>
              )}
              <Radio value="all-imported-users">Meus clientes - Base ERP ({totalImportedUsers} usuarios)</Radio>
            </Stack>
          </RadioGroup>
          <SubmitStepButton
            onSubmit={handleSubmit}
            isDisabled={
              !(
                (sendMethod === 'all-users' && (userListChoice === 'all-users' || users.length > 0)) ||
                (sendMethod === 'manual-list' && (emailList.trim().length > 0 || !!file)) ||
                (sendMethod === 'all-imported-users' && totalImportedUsers > 0)
              )
            }
          />
        </Box>
        {sendMethod === 'manual-list' && (
          <Box flex={1}>
            <Labeled
              label={'Lista de Emails'}
              helpText={'Insira os emails aqui. Cada email deve estar em uma linha'}
              footerText={emailList.split('\n').filter((s) => s !== '').length + ' emails.'}
            >
              <Textarea
                minHeight={'150px'}
                onChange={(e) => setEmailList(e.target.value)}
                minWidth={'450px'}
                value={emailList}
              />
              <VStack spacing={4}>
                <Input type="file" onChange={handleFileChange} display="none" id="fileInput" />
                <Button as="label" htmlFor="fileInput" width="auto" minHeight="30px" fontSize={'15px'}>
                  Carregar CSV
                </Button>
                {file && <p>{file.name}</p>}
              </VStack>
            </Labeled>
          </Box>
        )}
        {sendMethod === 'all-users' && userListChoice === 'specific-users' && <UsersTable setUsers={setUsers} />}
      </HStack>
    </>
  );
}

const MuiTheme = createTheme({
  palette: {
    mode: 'light',
  },
});

function UsersTable({ setUsers }: { setUsers: React.Dispatch<React.SetStateAction<GridRowSelectionModel>> }) {
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | undefined>();
  const [_search, _setSearch] = useState('');
  const [search, setSearch] = useState('');
  const [sortParam, setSort] = useState<string | undefined>(undefined);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  const { data = { results: [], count: 0 }, isLoading } = useUsers({
    page: paginationModel.page + 1,
    page_size: paginationModel.pageSize,
    search: search.replaceAll('.', '').replaceAll('-', '').replaceAll('/', ''),
    ordering: sortParam,
  });

  const handleSortChange = useCallback((sortModels: GridSortModel) => {
    const sortModel = sortModels[0];
    const snakeCaseField = sortModel.field.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();
    const sortAlt = sortModel.sort === 'desc' ? '-' : '';
    setSort(sortAlt + snakeCaseField);
  }, []);

  useEffect(() => {
    clearTimeout(searchTimeout);
    setSearchTimeout(
      setTimeout(() => {
        setSearch(_search);
        setPaginationModel({ ...paginationModel, page: 0 });
      }, 500)
    );
    return () => clearTimeout(searchTimeout);
  }, [_search]);

  useEffect(() => {
    setUsers(rowSelectionModel);
  }, [rowSelectionModel]);

  return (
    <Card spacing={5} width={'90%'} padding={'0px'} marginTop="40px">
      <AdaptiveStack padding={'20px'} align="center" width={'100%'}>
        <HStack width={'100%'}>
          <Input
            value={_search}
            onChange={(e) => _setSearch(e.target.value)}
            width={{ base: '100%', lg: '300px' }}
            variant="filled"
            placeholder={'Buscar por nome, email ou cpf'}
          />

          <Text whiteSpace="nowrap" color={'#a5a5a5'} fontSize={'8pt'}>
            ({data.count} total)
          </Text>
        </HStack>
        <Spacer />
      </AdaptiveStack>
      <ThemeProvider theme={MuiTheme}>
        <Box width={'100%'} height="70vh">
          <DataGrid
            initialState={{
              pagination: { paginationModel: { pageSize: 5 } },
            }}
            checkboxSelection={true}
            loading={isLoading}
            rows={data.results as GridRowsProp}
            columns={[
              { field: 'fullName', headerName: 'Nome', flex: 1, minWidth: 200 },
              { field: 'cpf', headerName: 'CPF/CNPJ', width: 150, valueFormatter: DataGridCPFFormatter },
              { field: 'email', headerName: 'Email', width: 200 },
            ]}
            paginationMode={'server'}
            sortingMode={'server'}
            rowCount={data.count}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            onSortModelChange={handleSortChange}
            rowHeight={32}
            pageSizeOptions={[5, 10, 25]}
            keepNonExistentRowsSelected
            onRowSelectionModelChange={setRowSelectionModel}
          />
        </Box>
      </ThemeProvider>
    </Card>
  );
}
